import {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type JSX,
  type MouseEvent as ReactMouseEvent,
} from 'react';
import { Box, styled } from '@mui/material';
import useWidth from '@/hooks/useWidth';
import { corePalette } from '@/themes/colours';
import { LABEL_WIDTH, TIMELINE_COLOURS } from '@/constants/PatientDashboard/patientTimeline';
import type {
  IDateRange, ITimelineMarker, ViewWindow,
} from '@/types/PatientDashboard/PatientTimeline.types';
import { clampView, dateToFraction, getMinViewWidth } from './PatientTimeline.util';

const OverviewContainer = styled(Box)({
  boxSizing: 'border-box',
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  width: '100%',
  padding: '4px 0',
  background: corePalette.white,
  borderRadius: '8px',
  // Inset shadow instead of a border so the track column lines up exactly
  // with the month axis / track rows (which have no such border).
  boxShadow: `inset 0 0 0 1px ${corePalette.grey30}`,
});

const OverviewLabel = styled(Box)({
  boxSizing: 'border-box',
  width: `${LABEL_WIDTH}px`,
  flexShrink: 0,
  paddingLeft: '12px',
  color: TIMELINE_COLOURS.labelMuted,
  fontSize: '10.5px',
  fontWeight: 700,
  lineHeight: '12px',
  letterSpacing: '0.525px',
  textTransform: 'uppercase',
});

const OverviewTrack = styled(Box)({
  position: 'relative',
  flex: 1,
  height: '40px',
  background: corePalette.grey30,
  borderRadius: '8px',
  userSelect: 'none',
  overflow: 'visible',
});

const OverviewWindow = styled(Box)({
  boxSizing: 'border-box',
  position: 'absolute',
  top: -4,
  bottom: -4,
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '2px 1px',
  background: `${corePalette.green10}4D`,
  border: `1px solid ${TIMELINE_COLOURS.accentGreen}`,
  borderRadius: '6px',
  cursor: 'grab',
  '&:active': {
    cursor: 'grabbing',
  },
});

const OverviewHandle = styled(Box)({
  alignSelf: 'stretch',
  flexShrink: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '10px',
  cursor: 'ew-resize',
});

const OverviewHandleBar = styled(Box)({
  width: '3px',
  height: '18px',
  borderRadius: '2px',
  background: TIMELINE_COLOURS.accentGreen,
});

const OVERVIEW_WINDOW_MIN_WIDTH = 24;

const OVERVIEW_NARROW_WINDOW_MIN_WIDTH = 14;

const OverviewDot = styled(Box)({
  position: 'absolute',
  width: '8px',
  height: '8px',
  borderRadius: '32px',
  transform: 'translateX(-50%)',
});

interface IProps {
  view: ViewWindow;
  eventMarkers: ITimelineMarker[];
  sampleMarkers: ITimelineMarker[];
  range: IDateRange;
  onViewChange: (view: ViewWindow) => void;
}

function Overview({
  view,
  eventMarkers,
  sampleMarkers,
  range,
  onViewChange,
}: IProps): JSX.Element {
  const [ref, width] = useWidth<HTMLDivElement>();
  const minViewWidth = useMemo(() => getMinViewWidth(range), [range]);
  const [viewStart, viewEnd] = view;
  const [isDragging, setIsDragging] = useState(false);
  const drag = useRef<{
    mode: 'pan' | 'left' | 'right';
    startX: number;
    start: ViewWindow;
  } | null>(null);

  const handleMouseMove = useCallback((event: MouseEvent): void => {
    if (!drag.current || !width) {
      return;
    }

    const delta = (event.clientX - drag.current.startX) / width;
    let [start, end] = drag.current.start;

    if (drag.current.mode === 'pan') {
      const currentWidth = end - start;
      start = Math.max(0, Math.min(1 - currentWidth, start + delta));
      end = start + currentWidth;
    }

    if (drag.current.mode === 'left') {
      start = Math.max(0, Math.min(end - minViewWidth, start + delta));
    }

    if (drag.current.mode === 'right') {
      end = Math.min(1, Math.max(start + minViewWidth, end + delta));
    }

    onViewChange(clampView([start, end], minViewWidth));
  }, [minViewWidth, onViewChange, width]);

  const handleMouseUp = useCallback((): void => {
    drag.current = null;
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (!isDragging) {
      return undefined;
    }

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [handleMouseMove, handleMouseUp, isDragging]);

  const handleMouseDown = (
    mode: 'pan' | 'left' | 'right',
  ) => (event: ReactMouseEvent<HTMLElement>): void => {
    event.stopPropagation();
    drag.current = {
      mode,
      startX: event.clientX,
      start: [...view] as ViewWindow,
    };
    setIsDragging(true);
  };

  const windowLayout = useMemo((): {
    style: { left: string; width: string };
    hasRoomForBothHandles: boolean;
  } => {
    if (!width) {
      return {
        style: { left: `${viewStart * 100}%`, width: `${(viewEnd - viewStart) * 100}%` },
        hasRoomForBothHandles: true,
      };
    }

    const spanWidth = (viewEnd - viewStart) * width;
    const hasRoomForBothHandles = spanWidth >= OVERVIEW_WINDOW_MIN_WIDTH;
    const windowWidth = Math.max(
      hasRoomForBothHandles ? OVERVIEW_WINDOW_MIN_WIDTH : OVERVIEW_NARROW_WINDOW_MIN_WIDTH,
      spanWidth,
    );
    const left = Math.max(0, Math.min(viewStart * width, width - windowWidth));

    return {
      style: { left: `${left}px`, width: `${windowWidth}px` },
      hasRoomForBothHandles,
    };
  }, [viewEnd, viewStart, width]);

  const handleResetView = (): void => onViewChange([0, 1]);

  const handlePanMouseDown = handleMouseDown('pan');
  const handleLeftHandleMouseDown = handleMouseDown('left');
  const handleRightHandleMouseDown = handleMouseDown('right');

  return (
    <OverviewContainer>
      <OverviewLabel>Overview</OverviewLabel>
      <OverviewTrack
        ref={ref}
        onDoubleClick={handleResetView}
      >
        {eventMarkers.map((marker) => (
          <OverviewDot
            key={`overview-event-${marker.id}`}
            top="12px"
            left={`${dateToFraction(marker.date, range) * 100}%`}
            style={{ background: marker.colour }}
          />
        ))}
        {sampleMarkers.map((marker) => (
          <OverviewDot
            key={`overview-sample-${marker.id}`}
            bottom="12px"
            left={`${dateToFraction(marker.date, range) * 100}%`}
            style={{ background: marker.colour }}
          />
        ))}
        <OverviewWindow onMouseDown={handlePanMouseDown} style={windowLayout.style}>
          <OverviewHandle onMouseDown={handleLeftHandleMouseDown}>
            <OverviewHandleBar />
          </OverviewHandle>
          {windowLayout.hasRoomForBothHandles && (
            <OverviewHandle onMouseDown={handleRightHandleMouseDown}>
              <OverviewHandleBar />
            </OverviewHandle>
          )}
        </OverviewWindow>
      </OverviewTrack>
    </OverviewContainer>
  );
}

export default memo(Overview);
